import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Info, Lock, ShoppingBag } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { Reveal, SectionTag, money } from "../components/bits";
import SEO from "../components/SEO";

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

const INPUT =
  "w-full rounded-xl border border-[#39FF14]/20 bg-[#0A150F] px-4 py-3 text-sm text-[#F0FDF4] placeholder-[#4ADE80]/40 outline-none transition-colors focus:border-[#39FF14]/70";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [method, setMethod] = useState("card");
  const [processing, setProcessing] = useState(false);
  const [config, setConfig] = useState({ card_enabled: true, paypal_enabled: false, paypal_client_id: "" });
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", state: "NSW", postcode: "" });

  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get("cancelled")) {
      toast.error("Payment cancelled — your cart is untouched.");
    }
  }, [searchParams]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const shippingValid =
    form.name && /.+@.+\..+/.test(form.email) && form.address && form.city && /^\d{4}$/.test(form.postcode);

  const payload = () => ({
    customer: form,
    items: items.map((i) => ({ slug: i.slug, sku: i.sku, qty: i.qty })),
  });

  const payByCard = async () => {
    if (!shippingValid || processing) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/checkout", { ...payload(), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Couldn't start secure checkout — please try again.");
      setProcessing(false);
    }
  };

  const payWithPayPal = async () => {
    if (!shippingValid || processing) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/orders", { ...payload(), payment_method: "paypal" });
      if (config.paypal_link) window.open(config.paypal_link, "_blank", "noopener");
      clear();
      navigate(`/order/${data.order_id}`, { state: { order: data } });
    } catch {
      toast.error("Something glitched — please try again.");
      setProcessing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center" data-testid="checkout-empty">
        <SEO title="Checkout — SuperTechLED" noindex />
        <ShoppingBag className="h-10 w-10 text-[#39FF14]/40" />
        <h1 className="font-display text-2xl font-bold text-[#F0FDF4]">Your cart is empty</h1>
        <Link to="/shop" className="rounded-full bg-[#39FF14] px-6 py-3 font-display text-sm font-bold text-[#050B07]">
          Browse the range
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 pb-28 pt-28 sm:px-8" data-testid="checkout-page">
      <SEO title="Checkout — SuperTechLED" noindex />
      <Reveal>
        <SectionTag>Guest checkout</SectionTag>
        <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl">Almost growing</h1>
      </Reveal>

      <div className="mt-10 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <Reveal delay={0.08} className="space-y-8">
          <div className="glass rounded-2xl p-6 sm:p-8">
            <h2 className="font-display text-lg font-bold text-[#F0FDF4]">Shipping details</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input data-testid="checkout-name-input" className={INPUT} placeholder="Full name" value={form.name} onChange={set("name")} />
              <input data-testid="checkout-email-input" className={INPUT} placeholder="Email" type="email" value={form.email} onChange={set("email")} />
              <input data-testid="checkout-address-input" className={`${INPUT} sm:col-span-2`} placeholder="Street address" value={form.address} onChange={set("address")} />
              <input data-testid="checkout-city-input" className={INPUT} placeholder="Suburb / City" value={form.city} onChange={set("city")} />
              <div className="grid grid-cols-2 gap-4">
                <select data-testid="checkout-state-select" className={INPUT} value={form.state} onChange={set("state")}>
                  {STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
                <input data-testid="checkout-postcode-input" className={INPUT} placeholder="Postcode" value={form.postcode} onChange={set("postcode")} />
              </div>
            </div>
          </div>

          <div className="glass rounded-2xl p-6 sm:p-8">
            <h2 className="font-display text-lg font-bold text-[#F0FDF4]">Payment</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                data-testid="card-tab"
                onClick={() => setMethod("card")}
                className={`flex items-center justify-center gap-2 rounded-xl border py-3.5 font-display text-sm font-bold transition-all ${
                  method === "card" ? "border-[#39FF14]/70 bg-[#0E1C13] text-[#39FF14]" : "border-[#39FF14]/15 text-[#9FB8A8] hover:border-[#39FF14]/40"
                }`}
              >
                <CreditCard className="h-4 w-4" /> Card
              </button>
              <button
                data-testid="paypal-tab"
                onClick={() => setMethod("paypal")}
                className={`flex items-center justify-center gap-2 rounded-xl border py-3.5 transition-all ${
                  method === "paypal" ? "border-[#39FF14]/70 bg-[#0E1C13]" : "border-[#39FF14]/15 hover:border-[#39FF14]/40"
                }`}
              >
                <span className="font-display text-sm font-black italic"><span className="text-[#38BDF8]">Pay</span><span className="text-[#F0FDF4]">Pal</span></span>
              </button>
            </div>

            {method === "card" ? (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5">
                <div className="rounded-xl border border-[#39FF14]/15 bg-[#0A150F] p-5 text-sm leading-relaxed text-[#9FB8A8]">
                  You'll be redirected to <span className="font-semibold text-[#F0FDF4]">Stripe's secure checkout</span> to
                  pay by credit or debit card — Visa, Mastercard, Amex, Apple Pay and Google Pay. Your card details never
                  touch our servers.
                </div>
              </motion.div>
            ) : config.paypal_enabled ? (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5" data-testid="paypal-buttons">
                {!shippingValid && (
                  <p className="mb-3 text-xs text-amber-300">Fill in your shipping details above first.</p>
                )}
                <PayPalScriptProvider options={{ clientId: config.paypal_client_id, currency: "AUD", intent: "capture" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                    disabled={!shippingValid}
                    forceReRender={[subtotal]}
                    createOrder={async () => {
                      const { data } = await api.post("/paypal/create-order", payload());
                      return data.id;
                    }}
                    onApprove={async (d) => {
                      setProcessing(true);
                      try {
                        const { data } = await api.post(`/paypal/capture/${d.orderID}`);
                        clear();
                        navigate(`/order/${data.order_id}`);
                      } catch {
                        toast.error("PayPal capture failed — please try again.");
                        setProcessing(false);
                      }
                    }}
                    onError={() => toast.error("PayPal hit a snag — please try again.")}
                  />
                </PayPalScriptProvider>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5">
                <div className="rounded-xl border border-[#38BDF8]/25 bg-[#0A150F] p-5 text-sm text-[#9FB8A8]">
                  Hit the button and we'll open <span className="font-semibold text-[#F0FDF4]">PayPal's secure payment page</span> in
                  a new tab — complete your <span className="text-[#86EFAC]">{money(subtotal)}</span> payment there and quote your
                  order number. We'll email your confirmation and tracking as soon as it lands.
                </div>
              </motion.div>
            )}

            <div className="mt-5 flex items-start gap-2 rounded-xl border border-[#39FF14]/20 bg-[#0E1C13]/60 px-4 py-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#39FF14]" />
              <p className="text-xs leading-relaxed text-[#86EFAC]">
                Card payments are processed live by Stripe in test mode — use card 4242 4242 4242 4242, any future
                expiry, any CVC.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass sticky top-24 rounded-2xl p-6 sm:p-8">
            <h2 className="font-display text-lg font-bold text-[#F0FDF4]">Order summary</h2>
            <ul className="mt-5 space-y-4">
              {items.map((i) => (
                <li key={i.key} className="flex items-center gap-3">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#050B07]">
                    <img src={i.image} alt={i.name} className="h-full w-full object-contain p-1" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-[#F0FDF4]">{i.name}</div>
                    <div className="font-mono2 text-[10px] uppercase tracking-widest text-[#4ADE80]">
                      {i.variantName} × {i.qty}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-[#F0FDF4]">{money(i.price * i.qty)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-2 border-t border-[#39FF14]/10 pt-4 text-sm">
              <div className="flex justify-between text-[#9FB8A8]">
                <span>Subtotal</span>
                <span data-testid="checkout-subtotal">{money(subtotal)}</span>
              </div>
              <div className="flex justify-between text-[#9FB8A8]">
                <span>Shipping (AU express)</span>
                <span className="text-[#39FF14]">FREE</span>
              </div>
              <div className="flex justify-between pt-2 font-display text-xl font-black text-[#F0FDF4]">
                <span>Total</span>
                <span data-testid="checkout-total">{money(subtotal)}</span>
              </div>
            </div>
            {!(method === "paypal" && config.paypal_enabled) && (
              <button
                data-testid="place-order-button"
                onClick={method === "card" ? payByCard : payWithPayPal}
                disabled={!shippingValid || processing}
                className={`mt-6 flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-sm font-bold transition-all ${
                  shippingValid && !processing
                    ? "bg-[#39FF14] text-[#050B07] hover:shadow-[0_0_36px_rgba(57,255,20,0.45)]"
                    : "cursor-not-allowed bg-[#39FF14]/20 text-[#39FF14]/40"
                }`}
              >
                {processing ? (
                  <>
                    <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: "linear" }} className="h-4 w-4 rounded-full border-2 border-[#050B07]/30 border-t-[#050B07]" />
                    Processing…
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    {method === "card" ? `Pay ${money(subtotal)} securely by card` : `Pay ${money(subtotal)} with PayPal`}
                  </>
                )}
              </button>
            )}
            <p className="mt-3 text-center font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#4ADE80]/60">
              Free AU express · 2-year warranty · 30-day returns
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
